import React from 'react';
import { Pencil, Check, X, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
export const EMPTY_BILL_TO = {
  name: '',
  company_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  tax_id: '',
};

// Only these columns are managed here; the backend treats a PUT as a partial
// update, so payment terms / credit limit / notes on the customer stay as-is.
const MANAGED_FIELDS = Object.keys(EMPTY_BILL_TO);

export function billToFromCustomer(customer) {
  const out = { ...EMPTY_BILL_TO };
  if (!customer) return out;
  for (const f of MANAGED_FIELDS) out[f] = customer[f] || '';
  return out;
}

export function billToPatch(billTo) {
  const patch = {};
  for (const f of MANAGED_FIELDS) patch[f] = (billTo[f] || '').trim();
  return patch;
}

export function billToChanged(original, current) {
  if (!original) return false;
  for (const f of MANAGED_FIELDS) {
    if ((original[f] || '').trim() !== (current[f] || '').trim()) return true;
  }
  return false;
}

/**
 * Inline "Bill To" editor for the invoice form.
 *
 * Read-only by default (matching the printed document) and switches to editable
 * inputs on demand so the header stays compact. Edits are held in a local draft
 * while editing, so Cancel really does discard them. Whatever is committed is
 * written back to the customer record — either immediately via "Save to
 * Customer", or automatically when the invoice itself is saved.
 */
export default function BillToEditor({ billTo, onChange, customerId, customerName, onSaveToCustomer }) {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState(billTo || EMPTY_BILL_TO);

  const startEditing = () => {
    setDraft(billTo || EMPTY_BILL_TO);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(billTo || EMPTY_BILL_TO);
    setEditing(false);
  };

  const set = (field) => (e) => setDraft(d => ({ ...(d || EMPTY_BILL_TO), [field]: e.target.value }));

  const commit = () => {
    if (onChange) onChange(draft);
    setEditing(false);
  };

  const saveToCustomer = async () => {
    if (!onSaveToCustomer) return;
    setSaving(true);
    try {
      await onSaveToCustomer(billToPatch(draft));
      if (onChange) onChange(draft);
      setEditing(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="inv-billto">
        <div className="inv-billto-label">
          Bill To
          <button type="button" className="inv-billto-edit" onClick={startEditing}>
            <Pencil size={11} /> Edit
          </button>
        </div>
        <div className="inv-billto-content" onDoubleClick={startEditing}>
          {billTo && billTo.name ? (
            <>
              <strong>{billTo.name}</strong>
              {billTo.company_name && <><br />{billTo.company_name}</>}
              {billTo.address_line1 && <><br />{billTo.address_line1}</>}
              {billTo.address_line2 && <><br />{billTo.address_line2}</>}
              {(billTo.city || billTo.state || billTo.postal_code) && (
                <><br />{[billTo.city, billTo.state, billTo.postal_code].filter(Boolean).join(', ')}</>
              )}
              {billTo.country && <><br />{billTo.country}</>}
              {billTo.phone && <><br />{billTo.phone}</>}
              {billTo.email && <><br />{billTo.email}</>}
              {billTo.tax_id && <><br />Tax ID: {billTo.tax_id}</>}
            </>
          ) : (
            <span className="inv-placeholder-text">
              {customerId
                ? 'No details on file — click Edit to add them.'
                : 'Select a customer, or click Edit to enter details.'}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="inv-billto">
      <div className="inv-billto-label">
        Bill To
        {customerId && (
          <span className="inv-billto-hint">
            <Building2 size={11} /> saved to {customerName || 'this customer'}
          </span>
        )}
      </div>
      <div className="inv-billto-form">
        <div className="inv-billto-row">
          <input className="inv-field-input" placeholder="Name" value={draft.name || ''} onChange={set('name')} />
          <input className="inv-field-input" placeholder="Company" value={draft.company_name || ''} onChange={set('company_name')} />
        </div>
        <div className="inv-billto-row">
          <input className="inv-field-input" placeholder="Email" type="email" value={draft.email || ''} onChange={set('email')} />
          <input className="inv-field-input" placeholder="Phone" value={draft.phone || ''} onChange={set('phone')} />
        </div>
        <input className="inv-field-input" placeholder="Address line 1" value={draft.address_line1 || ''} onChange={set('address_line1')} />
        <input className="inv-field-input" placeholder="Address line 2" value={draft.address_line2 || ''} onChange={set('address_line2')} />
        <div className="inv-billto-row">
          <input className="inv-field-input" placeholder="City" value={draft.city || ''} onChange={set('city')} />
          <input className="inv-field-input" placeholder="State / Province" value={draft.state || ''} onChange={set('state')} />
        </div>
        <div className="inv-billto-row">
          <input className="inv-field-input" placeholder="Postal code" value={draft.postal_code || ''} onChange={set('postal_code')} />
          <input className="inv-field-input" placeholder="Country" value={draft.country || ''} onChange={set('country')} />
        </div>
        <input className="inv-field-input" placeholder="Tax ID" value={draft.tax_id || ''} onChange={set('tax_id')} />
        <div className="inv-billto-actions">
          {customerId && onSaveToCustomer && (
            <button type="button" className="inv-btn inv-btn-secondary" onClick={saveToCustomer} disabled={saving}>
              <Check size={12} /> {saving ? 'Saving...' : 'Save to Customer'}
            </button>
          )}
          <button type="button" className="inv-btn inv-btn-primary" onClick={commit}>
            Done
          </button>
          <button type="button" className="inv-btn inv-btn-secondary" onClick={cancel}>
            <X size={12} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
